import React, { useState, useEffect } from "react";
import { Box, Typography, TextField, MenuItem, Grid, Card, IconButton, CircularProgress } from "@mui/material";
import { Phone, Mail, User, Award, MapPin, Briefcase } from "lucide-react";
import useConsultantSubscriptionStore from "../../store/consultant/useConsultantSubscriptionStore";
import { motion } from "framer-motion";
import toast from "react-hot-toast";

const ConsultantDiscoveryCard = ({ consultant }) => {
    const [isFlipped, setIsFlipped] = useState(false);

    const handleCall = () => {
        if (consultant.phone_number) {
            window.location.href = `tel:${consultant.phone_number}`;
        } else {
            toast.error("Phone number not available");
        }
    };

    const handleEmail = () => {
        if (consultant.email) {
            window.location.href = `mailto:${consultant.email}`;
        } else {
            toast.error("Email not available");
        }
    };

    return (
        <div
            className="relative w-full h-64 cursor-pointer perspective-1000"
            onMouseEnter={() => setIsFlipped(true)}
            onMouseLeave={() => setIsFlipped(false)}
            onClick={() => setIsFlipped(!isFlipped)}
        >
            <motion.div
                className="relative w-full h-full"
                initial={false}
                animate={{ rotateY: isFlipped ? 180 : 0 }}
                transition={{ duration: 0.6, type: "spring" }}
                style={{ transformStyle: "preserve-3d" }}
            >
                {/* Front Side */}
                <Card
                    className="absolute w-full h-full backface-hidden shadow-lg"
                    style={{ backfaceVisibility: "hidden" }}
                >
                    <Box className="h-full bg-gradient-to-br from-blue-600 to-blue-800 p-6 flex flex-col justify-between text-white relative overflow-hidden">
                        {/* Decorative elements */}
                        <div className="absolute top-0 right-0 w-32 h-32 bg-white opacity-5 rounded-full -mr-16 -mt-16" />
                        <div className="absolute bottom-0 left-0 w-24 h-24 bg-white opacity-5 rounded-full -ml-12 -mb-12" />

                        {/* Profile Image */}
                        <Box className="flex items-center gap-4 relative z-10">
                            <img
                                src={consultant.profile_image || "/assets/Support.png"}
                                alt={consultant.name}
                                className="w-16 h-16 rounded-full border-4 border-white shadow-lg object-cover"
                                onError={(e) => {
                                    e.target.src = "/assets/Support.png";
                                }}
                            />
                            <Box>
                                <Typography variant="h6" className="font-bold text-white">
                                    {consultant.name}
                                </Typography>
                                <Typography variant="caption" className="text-blue-100">
                                    {consultant.consultant_id}
                                </Typography>
                            </Box>
                        </Box>

                        {/* Qualification & Experience */}
                        <Box className="space-y-2 relative z-10">
                            <Box className="flex items-center gap-2">
                                <Award size={18} className="text-blue-200" />
                                <Typography variant="body2" className="text-white font-medium">
                                    {consultant.qualification || "Professional Engineer"}
                                </Typography>
                            </Box>
                            <Box className="flex items-center gap-2">
                                <Briefcase size={18} className="text-blue-200" />
                                <Typography variant="body2" className="text-white">
                                    {consultant.experience || "0"} {consultant.experience === "1" ? "Year" : "Years"} Experience
                                </Typography>
                            </Box>
                            <Box className="flex items-center gap-2">
                                <MapPin size={18} className="text-blue-200" />
                                <Typography variant="body2" className="text-white">
                                    {consultant.taluk}, {consultant.district}
                                </Typography>
                            </Box>
                        </Box>

                        {/* Hover Indicator */}
                        <Box className="text-center">
                            <Typography variant="caption" className="text-blue-100 italic">
                                Hover to view contact options
                            </Typography>
                        </Box>
                    </Box>
                </Card>

                {/* Back Side */}
                <Card
                    className="absolute w-full h-full backface-hidden shadow-lg"
                    style={{
                        backfaceVisibility: "hidden",
                        transform: "rotateY(180deg)",
                    }}
                >
                    <Box className="h-full bg-gradient-to-br from-gray-50 to-gray-100 p-6 flex flex-col justify-center items-center relative overflow-hidden">
                        {/* Decorative Pattern */}
                        <div className="absolute inset-0 opacity-5">
                            <div className="absolute top-0 left-0 w-full h-full" style={{
                                backgroundImage: "radial-gradient(circle, #000 1px, transparent 1px)",
                                backgroundSize: "20px 20px"
                            }} />
                        </div>

                        <Typography variant="h6" className="font-bold text-gray-800 mb-6 relative z-10">
                            Contact {consultant.name?.split(' ')[0]}
                        </Typography>

                        {/* Contact Buttons */}
                        <Box className="flex flex-col gap-3 w-full max-w-xs relative z-10">
                            <motion.button
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleCall();
                                }}
                                className="flex items-center justify-center gap-3 bg-green-600 hover:bg-green-700 text-white py-4 px-6 rounded-xl shadow-lg transition-all"
                            >
                                <Phone size={20} />
                                <span className="font-semibold">Call Us</span>
                            </motion.button>

                            <motion.button
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleEmail();
                                }}
                                className="flex items-center justify-center gap-3 bg-blue-600 hover:bg-blue-700 text-white py-4 px-6 rounded-xl shadow-lg transition-all"
                            >
                                <Mail size={20} />
                                <span className="font-semibold">Email Us</span>
                            </motion.button>
                        </Box>

                        {/* Contact Info Display */}
                        <Box className="mt-6 text-center relative z-10">
                            <Typography variant="caption" className="text-gray-600 block">
                                {consultant.phone_number || "Phone not available"}
                            </Typography>
                            <Typography variant="caption" className="text-gray-600 block">
                                {consultant.email || "Email not available"}
                            </Typography>
                        </Box>
                    </Box>
                </Card>
            </motion.div>
        </div>
    );
};

const ConsultantDiscovery = () => {
    const { activeConsultants, isLoading, fetchActiveConsultants } = useConsultantSubscriptionStore();
    const [district, setDistrict] = useState("");
    const [taluk, setTaluk] = useState("");

    // Tamil Nadu districts and taluks mapping
    const districtsData = {
        "Tirunelveli": ["Tirunelveli", "Palayamkottai", "Nanguneri", "Ambasamudram", "Cheranmahadevi"],
        "Thoothukudi": ["Thoothukudi", "Kovilpatti", "Srivaikuntam", "Ottapidaram", "Vilathikulam"],
        "Kanyakumari": ["Nagercoil", "Padmanabhapuram", "Kalkulam", "Vilavancode"],
        "Chennai": ["Egmore", "Mylapore", "Ambattur", "Perambur"],
        "Coimbatore": ["Coimbatore North", "Coimbatore South", "Pollachi", "Mettupalayam"],
        "Madurai": ["Madurai North", "Madurai South", "Melur", "Usilampatti"],
    };

    useEffect(() => {
        fetchActiveConsultants();
    }, []);

    const handleSearch = () => {
        fetchActiveConsultants(district || null, taluk || null);
    };

    const handleDistrictChange = (e) => {
        setDistrict(e.target.value);
        setTaluk(""); // Reset taluk when district changes
        fetchActiveConsultants(e.target.value || null, null);
    };

    const handleTalukChange = (e) => {
        setTaluk(e.target.value);
        fetchActiveConsultants(district || null, e.target.value || null);
    };

    return (
        <Box className="p-4 lg:p-8 max-w-7xl mx-auto">
            <Typography variant="h4" className="font-bold mb-2 text-gray-900">
                Find Construction Consultants
            </Typography>
            <Typography variant="body1" className="text-gray-600 mb-6">
                Search for verified consultants in your area
            </Typography>

            {/* Search Filters */}
            <Card className="mb-8 shadow-md">
                <Box className="p-6">
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={5}>
                            <TextField
                                select
                                fullWidth
                                label="Select District"
                                value={district}
                                onChange={handleDistrictChange}
                                variant="outlined"
                            >
                                <MenuItem value="">All Districts</MenuItem>
                                {Object.keys(districtsData).map((dist) => (
                                    <MenuItem key={dist} value={dist}>
                                        {dist}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={12} md={5}>
                            <TextField
                                select
                                fullWidth
                                label="Select Taluk"
                                value={taluk}
                                onChange={handleTalukChange}
                                variant="outlined"
                                disabled={!district}
                            >
                                <MenuItem value="">All Taluks</MenuItem>
                                {district &&
                                    districtsData[district]?.map((tlk) => (
                                        <MenuItem key={tlk} value={tlk}>
                                            {tlk}
                                        </MenuItem>
                                    ))}
                            </TextField>
                        </Grid>
                    </Grid>

                    {(district || taluk) && (
                        <Box className="mt-4 flex items-center gap-2">
                            <Typography variant="body2" className="text-gray-600">
                                Showing consultants in:
                            </Typography>
                            {district && (
                                <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">
                                    {district}
                                </span>
                            )}
                            {taluk && (
                                <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium">
                                    {taluk}
                                </span>
                            )}
                        </Box>
                    )}
                </Box>
            </Card>

            {/* Results Section */}
            {isLoading ? (
                <Box className="flex justify-center items-center min-h-[400px]">
                    <CircularProgress />
                </Box>
            ) : activeConsultants.length === 0 ? (
                <Card>
                    <Box className="text-center py-16">
                        <User size={48} className="mx-auto text-gray-400 mb-4" />
                        <Typography variant="h6" className="text-gray-600 mb-2">
                            No consultants found
                        </Typography>
                        <Typography variant="body2" className="text-gray-500">
                            {district || taluk
                                ? "Try adjusting your search filters"
                                : "No active consultants available at the moment"}
                        </Typography>
                    </Box>
                </Card>
            ) : (
                <>
                    <Typography variant="body2" className="text-gray-600 mb-4">
                        Found {activeConsultants.length} consultant{activeConsultants.length !== 1 ? "s" : ""}
                    </Typography>
                    <Grid container spacing={4}>
                        {activeConsultants.map((consultant) => (
                            <Grid item xs={12} sm={6} lg={4} key={consultant.id}>
                                <ConsultantDiscoveryCard consultant={consultant} />
                            </Grid>
                        ))}
                    </Grid>
                </>
            )}

            {/* Custom CSS for flip card */}
            <style jsx>{`
                .perspective-1000 {
                    perspective: 1000px;
                }
                .backface-hidden {
                    backface-visibility: hidden;
                    -webkit-backface-visibility: hidden;
                }
            `}</style>
        </Box>
    );
};

export default ConsultantDiscovery;
